import React from 'react';
import { Grid, Tooltip, Typography, IconButton, Button, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';

const rowTextStyle = {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '13px!important',
    lineHeight: '22px',
    letterSpacing: '-0.04em',
    color: '#000453',
};

const PatientRow = ({
    patient,
    selectedRef,
    isSelected,
    onEdit,
    onArchive,
    onView,
    hasWhiteboard,
    isMobileOrTablet,
    hideWhiteboardColumn = false,
    whiteboardCount = 0,
    customColumnWidths,
    compactStatus,
    isFirstItem = false,
    isLastItem = false,
}) => {
    console.warn(patient);
    const getColumnWidth = (key) => {
        if (customColumnWidths?.[key]) return customColumnWidths[key];
        if (isMobileOrTablet) {
            return key === 'patientName' || key === 'patientEmail' ? 3 : 2;
        }
        return 1;
    };

    return isMobileOrTablet ? (
        <Box
            ref={isSelected ? selectedRef : null}
            sx={{
                backgroundColor: isSelected ? '#E8EAF6' : '#FFFFFF',
                p: 1,
                borderBottom: isLastItem ? 'none' : '1px solid #c6c6c6',
                borderTopLeftRadius: isFirstItem ? '8px' : 0,
                borderTopRightRadius: isFirstItem ? '8px' : 0,
                borderBottomLeftRadius: isLastItem ? '8px' : 0,
                borderBottomRightRadius: isLastItem ? '8px' : 0,
                color: '#000453',
            }}
        >
            <Grid container sx={{ padding: 1 }}>
                <Grid item xs={12} display="flex" flexDirection="column">
                    <Typography fontWeight="bold" mb={0.5}>
                        {patient.patientName}
                    </Typography>
                    <Typography fontWeight="bold" mb={1}>
                        {patient.patientSurname}
                    </Typography>
                    <Typography fontWeight="bold" mb={1}>
                        {patient.patientEmail}
                    </Typography>
                    <Typography mb={1}>{patient.clinicPatientId}</Typography>

                    <Box display={'flex'} justifyContent={'space-between'}>
                        <Box
                            sx={{
                                width: '72px',
                                height: '30px',
                                borderRadius: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontSize: '12.78px',
                                lineHeight: '16px',
                                textAlign: 'center',
                                backgroundColor:
                                    patient.patientStatus === 'active'
                                        ? 'rgba(0, 137, 0, 0.15)'
                                        : 'rgba(220, 0, 37, 0.15)',
                                color: patient.patientStatus === 'active' ? '#008900' : '#DC0025',
                                mb: 2,
                            }}
                        >
                            {patient.patientStatus === 'active' ? 'Active' : 'Archived'}
                        </Box>

                        {!hideWhiteboardColumn && (
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={!hasWhiteboard}
                                onClick={() => onView(patient)}
                                sx={{
                                    width: '180px',
                                    height: '30px',
                                    fontWeight: 500,
                                    fontFamily: 'Inter',
                                    fontSize: '13px',
                                    lineHeight: '16px',
                                    textTransform: 'none',
                                    color: '#FF669B',
                                    borderColor: '#FF669B',
                                    borderRadius: '8px',
                                    backgroundColor: '#FFFFFF',
                                    '&:hover': {
                                        borderColor: '#FF669B',
                                    },
                                    '&.Mui-disabled': {
                                        color: '#C4C4C4',
                                        borderColor: '#C4C4C4',
                                        backgroundColor: '#F1F1F1',
                                    },
                                    mb: 1,
                                }}
                            >
                                View Whiteboards ({whiteboardCount || 0})
                            </Button>
                        )}
                    </Box>

                    <Box display="flex" gap={1} position={'absolute'} right={24}>
                        <Tooltip title="Edit patient" arrow>
                            <IconButton onClick={() => onEdit(patient)} sx={{ color: '#000453' }}>
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Archive patient" arrow>
                            <IconButton onClick={() => onArchive(patient.patientId)} sx={{ color: '#000453' }}>
                                <ArchiveIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    ) : (
        <Box sx={{ minWidth: '1100px' }}>
            <Grid
                container
                alignItems="center"
                ref={isSelected ? selectedRef : null}
                sx={{
                    backgroundColor: isSelected ? '#E8EAF6' : 'transparent',
                    height: '60px',
                    px: 2,
                    borderTop: '1.4px solid #0000001A',
                    '&:hover': { backgroundColor: '#EFEFEF' },
                }}
            >
                {/* Name */}
                <Grid item xs={getColumnWidth('patientName')}>
                    <Typography
                        sx={{
                            ...rowTextStyle,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {patient.patientName}
                    </Typography>
                </Grid>

                {/* Surname */}
                {!isMobileOrTablet && (
                    <Grid item xs={getColumnWidth('patientSurname')}>
                        <Typography
                            sx={{
                                ...rowTextStyle,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {patient.patientSurname}
                        </Typography>
                    </Grid>
                )}

                {/* Only show these columns on desktop */}
                {!isMobileOrTablet && (
                    <>
                        <Grid item xs={getColumnWidth('patientEmail')}>
                            <Typography sx={rowTextStyle}>{patient.patientEmail}</Typography>
                        </Grid>

                        <Grid item xs={getColumnWidth('clinicPatientId')}>
                            <Typography sx={rowTextStyle}>{patient.clinicPatientId}</Typography>
                        </Grid>
                    </>
                )}

                {/* Status */}
                <Grid item xs={getColumnWidth('patientStatus')}>
                    <Box
                        sx={{
                            width: compactStatus
                                ? isMobileOrTablet
                                    ? '30px'
                                    : '72px'
                                : isMobileOrTablet
                                  ? '30px'
                                  : '96px',
                            height: '30px',
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            fontSize: '12px',
                            lineHeight: '16px',
                            textAlign: 'center',
                            backgroundColor:
                                patient.patientStatus === 'active' ? 'rgba(0, 137, 0, 0.15)' : 'rgba(220, 0, 37, 0.15)',
                            color: patient.patientStatus === 'active' ? '#008900' : '#DC0025',
                        }}
                    >
                        {patient.patientStatus === 'active' && !isMobileOrTablet
                            ? 'Active'
                            : !isMobileOrTablet
                              ? 'Archived'
                              : null}
                    </Box>
                </Grid>

                {/* Operation */}
                <Grid item xs={getColumnWidth('actions')}>
                    <Tooltip title="Edit patient" arrow>
                        <IconButton onClick={() => onEdit(patient)} sx={{ color: '#000453' }}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Archive patient" arrow>
                        <IconButton onClick={() => onArchive(patient.patientId)} sx={{ color: '#000453' }}>
                            <ArchiveIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>

                {/* Whiteboard View */}
                {!hideWhiteboardColumn && (
                    <Grid item xs={getColumnWidth('whiteboards')}>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={!hasWhiteboard}
                            onClick={() => onView(patient)}
                            sx={{
                                width: '102px',
                                height: '30px',
                                fontWeight: 500,
                                fontFamily: 'Inter',
                                fontSize: '12px',
                                lineHeight: '16px',
                                textTransform: 'none',
                                color: '#FF669B',
                                borderColor: '#FF669B',
                                borderRadius: '8px',
                                backgroundColor: '#FFFFFF',
                                '&:hover': {
                                    backgroundColor: '#FFFFFF',
                                    borderColor: '#FF669B',
                                },
                                '&.Mui-disabled': {
                                    color: '#C4C4C4',
                                    borderColor: '#C4C4C4',
                                    backgroundColor: '#F1F1F1',
                                },
                            }}
                        >
                            View ({whiteboardCount || 0})
                        </Button>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default PatientRow;
